import { IProductTax, ProductDetailsCart } from '../models';

/**
 * Universal points/USD conversion using basis-points (BPS) conversion rate.
 *
 *   Points = USD * (10000 / rate)
 *
 * `rate` is in BPS: 100 = 1:1 standard, 125 = +25% accelerator, 150 = +50%, etc.
 * The decimal part is always rounded DOWN (floor), so e.g. 89866.6667 → 89866.
 * Returns 0 when rate is missing/non-positive or usd is non-finite.
 */
export function usdToPoints(usd: number, rate: number): number {
  if (!rate || rate <= 0 || !Number.isFinite(usd)) return 0;
  return Math.floor((usd * 10000) / rate);
}

export const pickTaxPointsIfProvidedOrCalculateFromUsdTax = (
  tax: IProductTax,
  conversionRatePointsToOneUsd: number,
): number => {
  try {
    return tax.taxAmountPoints ?? usdToPoints(tax.taxAmountUSD, conversionRatePointsToOneUsd);
  } catch {
    return 0;
  }
};

/**
 * Effective (accelerated) tax points the user actually pays: prefers the
 * discounted per-line-item value, falling back to the standard tax points.
 *
 * `discountedTaxAmountPoints` is only present when the cart has an accelerated
 * rate; when absent it falls back to {@link pickTaxPointsIfProvidedOrCalculateFromUsdTax}.
 */
export const pickEffectiveTaxPoints = (tax: IProductTax, conversionRatePointsToOneUsd: number): number =>
  tax.discountedTaxAmountPoints ?? pickTaxPointsIfProvidedOrCalculateFromUsdTax(tax, conversionRatePointsToOneUsd);

export function computeCartSubtotalUsd(productCard: ProductDetailsCart | null): number {
  return productCard?.subtotalDiscountedPrice ?? productCard?.subtotalPrice ?? 0;
}

export function computeCartSubtotalPoints(cartSubtotalUsd: number, conversionRate: number): number {
  return usdToPoints(cartSubtotalUsd, conversionRate);
}

export function computeCartSubtotalUsdWithTax(cartSubtotalUsd: number, productTax?: IProductTax): number {
  return cartSubtotalUsd + (productTax?.taxAmountUSD || 0);
}

export function computeCartSubtotalPointsWithTax(
  cartSubtotalPoints: number,
  productTax: IProductTax,
  conversionRate: number,
): number {
  return Math.floor(cartSubtotalPoints + pickTaxPointsIfProvidedOrCalculateFromUsdTax(productTax, conversionRate));
}

export const pickLowestValue = (val1: number, val2: number): number => {
  return val1 > val2 ? val2 : val1;
};

/**
 * Calculates product points via the universal BPS formula (see {@link usdToPoints}).
 *
 * `oldPoints` (strikethrough) is only returned when the product's rate differs
 * from the user's base rate — it represents the points the user would have
 * earned at their base rate, used to visualise the accelerator benefit.
 *
 * @param price - Price in USD.
 * @param productConversionRate - Product's rate (BPS). When undefined, falls back to user's rate.
 * @param userConversionRate - User's base rate (BPS).
 */
export function calculateProductPoints(
  price: number,
  productConversionRate: number | undefined,
  userConversionRate: number,
): {
  newPoints: number;
  oldPoints: number | null;
  hasSpecialRate: boolean;
} {
  const effectiveRate = productConversionRate ?? userConversionRate;
  const hasSpecialRate = productConversionRate !== undefined && productConversionRate !== userConversionRate;

  return {
    newPoints: usdToPoints(price, effectiveRate),
    oldPoints: hasSpecialRate ? usdToPoints(price, userConversionRate) : null,
    hasSpecialRate,
  };
}

/**
 * Calculates cart total points considering individual product conversion rates.
 *
 * @param productCard - The cart data containing products
 * @param userConversionRate - User's default conversion rate
 * @param useDiscountedPrice - Whether to use discounted prices or regular prices
 * @returns Total points using product-specific conversion rates
 */
export function calculateCartTotalPointsWithRates(
  productCard: ProductDetailsCart | null,
  userConversionRate: number,
  useDiscountedPrice = false,
): number {
  const products = productCard?.products ?? [];
  if (products.length === 0) return 0;

  return products.reduce((total, product) => {
    // `price`/`discountedPrice` are line totals (already include quantity).
    const price = useDiscountedPrice && product.discountedPrice ? product.discountedPrice : product.price;
    const pointsCalc = calculateProductPoints(price, product.pointConversionRate, userConversionRate);
    return total + pointsCalc.newPoints;
  }, 0);
}

/**
 * Calculates cart total points as if every product was at the user's base rate.
 * Used to render the "old" strikethrough points when products are accelerated.
 */
export function calculateCartTotalPointsAtUserRate(
  productCard: ProductDetailsCart | null,
  userConversionRate: number,
  useDiscountedPrice = false,
): number {
  const products = productCard?.products ?? [];
  if (products.length === 0) return 0;

  return products.reduce((total, product) => {
    // `price`/`discountedPrice` are line totals (already include quantity).
    const price = useDiscountedPrice && product.discountedPrice ? product.discountedPrice : product.price;
    return total + usdToPoints(price, userConversionRate);
  }, 0);
}

/**
 * Returns true when any product in the cart has a rate that differs from the
 * user's base rate (i.e. has an accelerator applied).
 */
export function hasSpecialRateProducts(
  productCard: ProductDetailsCart | null,
  userConversionRate: number,
): boolean {
  const products = productCard?.products ?? [];
  return products.some((p) => p.pointConversionRate !== undefined && p.pointConversionRate !== userConversionRate);
}

/**
 * Calculates the USD value covered by redeeming a specific number of points,
 * taking into account product-specific conversion rates (points acceleration).
 *
 * This applies points PREFERENTIALLY to the most accelerated products first to maximize
 * value, then to standard products, then finally to tax.
 *
 * @param pointsToRedeem - Number of points the user wants to redeem
 * @param productCard - The cart data containing products
 * @param userConversionRate - User's default conversion rate
 * @param productTax - Tax information
 * @returns USD value that will be covered by the redeemed points
 */
export function calculateUsdValueOfRedeemedPoints(
  pointsToRedeem: number,
  productCard: ProductDetailsCart | null,
  userConversionRate: number,
  productTax: IProductTax | null,
): number {
  if (!pointsToRedeem || pointsToRedeem <= 0) return 0;

  const products = productCard?.products ?? [];
  if (products.length === 0) return 0;

  // Check if cart has discounted prices
  const hasDiscountedPrice =
    productCard?.subtotalDiscountedPrice !== undefined &&
    productCard?.subtotalDiscountedPrice !== null &&
    productCard?.subtotalDiscountedPrice > 0;

  // Build product buckets with their USD value, points cost, and rate
  interface ProductBucket {
    usdValue: number;
    pointsCost: number;
    conversionRate: number;
    productName: string;
  }

  const productBuckets: ProductBucket[] = products.map((product) => {
    // `price`/`discountedPrice` are line totals (already include quantity).
    const price = hasDiscountedPrice && product.discountedPrice ? product.discountedPrice : product.price;
    const usdValue = price;
    const pointsCalc = calculateProductPoints(price, product.pointConversionRate, userConversionRate);
    const pointsCost = pointsCalc.newPoints;
    const conversionRate = product.pointConversionRate ?? userConversionRate;

    return {
      usdValue,
      pointsCost,
      conversionRate,
      productName: product.name || 'Unknown',
    };
  });

  // Sort by conversion rate (ascending) - best rate (most accelerated) first
  productBuckets.sort((a, b) => a.conversionRate - b.conversionRate);

  let usdCovered = 0;
  let remainingPoints = pointsToRedeem;

  // STEP 1: Apply points to products in order of best rate (maximize value)
  for (const bucket of productBuckets) {
    if (remainingPoints <= 0) break;

    const pointsToApply = Math.min(remainingPoints, bucket.pointsCost);
    const proportionCovered = pointsToApply / bucket.pointsCost;
    const usdFromBucket = bucket.usdValue * proportionCovered;

    usdCovered += usdFromBucket;
    remainingPoints -= pointsToApply;
  }

  // STEP 2: Apply remaining points to tax (accelerated rate when available)
  if (remainingPoints > 0 && productTax) {
    const taxPoints = pickEffectiveTaxPoints(productTax, userConversionRate);
    const taxUsd = productTax.taxAmountUSD || 0;

    if (taxPoints > 0) {
      const pointsAppliedToTax = Math.min(remainingPoints, taxPoints);
      const proportionOfTax = pointsAppliedToTax / taxPoints;
      const usdFromTax = taxUsd * proportionOfTax;

      usdCovered += usdFromTax;
      remainingPoints -= pointsAppliedToTax;
    }
  }

  return usdCovered;
}
